import type { CSSProperties } from "react";
import { EXAMPLE_JOB } from "@/lib/example";
import { EXAMPLE_FORM, leftSummary, type ExampleField, type FillMode } from "@/lib/exampleFill";

const TAG =
    "inline-flex shrink-0 items-center gap-1.5 rounded-full px-2 py-px text-[11px] font-semibold";

/** The extension's tag on a question: green where it filled it, blue where it left it. */
function Tag({ filled }: { filled: boolean }) {
    return (
        <span
            className={`${TAG} ${filled ? "bg-tint-green text-brand-ink" : "bg-tint-info text-info"}`}
        >
            <span className="size-1.5 rounded-full bg-current" aria-hidden="true" />
            {filled ? "Your envoy" : "Yours to answer"}
        </span>
    );
}

/** One question of the form: its label and tag, the control as the fill left it, and where the answer came from. */
function Field({ field, mode, step }: { field: ExampleField; mode: FillMode; step: number }) {
    const answer = field.fills[mode];
    const wide = field.input === "long" || field.input === "file" || field.label.length > 30;
    return (
        <div
            role="group"
            aria-label={field.label}
            className={`flow-step flex flex-col gap-1.5 ${wide ? "col-span-2 max-[640px]:col-span-1" : ""}`}
            style={{ animationDelay: `${step * 60}ms` } as CSSProperties}
        >
            <span className="flex items-start justify-between gap-2 text-[13px] font-semibold">
                {field.label}
                <Tag filled={answer !== null} />
            </span>
            <div
                className={`flex min-h-[40px] items-center gap-2 rounded-[10px] border px-3 py-2 text-[13.5px] leading-[1.5] ${
                    answer
                        ? "border-green bg-card shadow-[0_0_0_3px_var(--color-tint-green)]"
                        : "border-info bg-tint-info text-fg-faint"
                } ${field.input === "long" ? "min-h-[88px] items-start" : ""}`}
            >
                {answer ? (
                    <>
                        {field.input === "file" && (
                            <span className="rounded-[6px] bg-card-raised px-1.5 py-px font-mono text-[11px] font-bold text-fg-muted">
                                PDF
                            </span>
                        )}
                        <span className="min-w-0 flex-1">{answer.value}</span>
                        {field.input === "select" && (
                            <span className="text-fg-faint" aria-hidden="true">
                                ▾
                            </span>
                        )}
                    </>
                ) : (
                    field.yours
                )}
            </div>
            {answer?.from && <span className="text-[12px] text-fg-faint">{answer.from}</span>}
        </div>
    );
}

/**
 * The example job's application form as the extension leaves it after
 * the fill chosen: every field it filled ringed green and tagged "Your
 * envoy", every field it left ringed blue and tagged "Yours to answer",
 * and the verification and Submit the person's. A new fill replays.
 */
export default function ExampleForm({ mode }: { mode: FillMode }) {
    return (
        <section
            aria-label="Example application, filled"
            className="flex flex-col gap-4 rounded-2xl border border-line bg-card px-6 py-[22px] shadow-card max-[560px]:px-4"
        >
            <header className="flex flex-wrap items-end justify-between gap-x-4 gap-y-1">
                <div className="flex flex-col gap-0.5">
                    <h3 className="m-0 font-display text-[20px] font-bold tracking-[-0.02em]">
                        Apply for {EXAMPLE_JOB.title}
                    </h3>
                    <p className="m-0 text-[13px] text-fg-muted">
                        <span className="font-semibold text-fg">{EXAMPLE_JOB.company}</span>, on its
                        own careers page
                    </p>
                </div>
                <p className="m-0 text-[13px] font-semibold text-fg-muted" aria-live="polite">
                    {leftSummary(mode)}
                </p>
            </header>
            <div key={mode} className="grid grid-cols-2 gap-x-4 gap-y-4 max-[640px]:grid-cols-1">
                {EXAMPLE_FORM.map((field, index) => (
                    <Field key={field.label} field={field} mode={mode} step={index} />
                ))}
            </div>
            <footer className="flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-line-soft pt-4">
                <span className="inline-flex h-[38px] items-center rounded-[10px] border border-line px-3 text-[13px] text-fg-muted">
                    Verification
                </span>
                <span className="inline-flex h-[38px] items-center rounded-[10px] bg-fg px-4 text-[13px] font-semibold text-card">
                    Submit application
                </span>
                <span className="flex-[1_1_220px] text-[13px] text-fg-muted">
                    The verification and Submit are yours. Your envoy never touches either.
                </span>
            </footer>
        </section>
    );
}
